import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, Paper, Grid } from '@mui/material';
import ProductCommentListTableHeader from './ProductCommentListTable/ProductCommentListTableHeaderV2';
import ProductCommentListTableBody from './ProductCommentListTable/ProductCommentListTableBody';
import { SCUIContent } from '../../../organisms';
import TimeHelper from '../../../../utils/time';
import { CommentQuery } from '../../../../services/query';
import {
    enumCommentGrievanceStatusFlowStatusName,
    enumCommentStatusFlowStatusName,
} from '../../../../types/enums/status';
import { useTranslation } from 'react-i18next';
import ProductCommentFilterComponentV2 from './ProductCommentFilterComponentV2';
import { SCUILoadingLinear, SCUIPagination } from '../../../atoms';
import { SCUIBreadCrumb } from '../../SCUIBreadCrumb/SCUIBreadCrumb';
import { useLookupLists } from '../../../../pages/ProductComments/CommentFilter/helper';
import Swal from 'sweetalert2';

type ProductCommentListRequest = {
    id?: string;
    specialCode?: string;
    colorCode?: string;
    createDate?: string;
    endDate?: string;
    starScore?: any;
    commentStatus?: { id: number }[];
    tagName?: any[];
    operator?: string;
    grievanceStatus?: { id: number; value: string }[];
    isAgent: any;
    userEmail?: string;
    CustomerId?: any;
    hasPhoto?: number;
    subBrandName?: string;
    isShipped?: boolean;
};

const ProductCommentListV2: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [commentList, setCommentList] = useState<any>([]);
    const [pageCount, setPageCount] = useState<any>();
    const [tagNames, setTagNames] = React.useState([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [params, setParams] = useState<any>({
        id: '',
        specialCode: '',
        colorCode: '',
        createDate: '',
        endDate: '',
        starScore: null,
        commentStatus: [{ id: 1 }],
        tagName: [],
        operator: '',
        grievanceStatus: [{ id: 2, value: 'Tümü' }],
        isAgent: null,
        userEmail: '',
        CustomerId: null,
        hasPhoto: undefined,
        subBrandName: '',
        isShipped: false,
    });

    const { t } = useTranslation();

    const enumCommentName: any = {
        1: t('general.waitingForApproval'),
        2: t('general.approved'),
        3: t('general.declined'),
    };

    const { grievanceStatusLookupList } = useLookupLists();

    const handleChangeRows = (event: any) => {
        setPage(0);
        setParams((prevParams: any) => ({
            ...prevParams,
            paginationInfo: {
                ...prevParams.paginationInfo,
                currentPage: 1,
                rowsPerPage: parseInt(event.target.value, 10),
            },
        }));
        setRowsPerPage(parseInt(event.target.value, 10));
    };

    const GetCommentTicketList = async () => {
        try {
            let ticketRes: any = await CommentQuery.GetCommentTicketList();

            if (ticketRes) {
                setTagNames(ticketRes.data || []);
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    // Helper function to add basic pagination parameters
    const addPaginationParams = (pageValue: number, rowsPerPageValue: number, totalPageCount?: number) => {
        const page = Number(pageValue) + 1;
        const size = totalPageCount || rowsPerPageValue;
        return `page=${page}&size=${size}&`;
    };

    // Helper function to add simple string parameters
    const addStringParams = (request: ProductCommentListRequest) => {
        let params = '';
        const stringFields = [
            { key: 'id', param: 'id' },
            { key: 'specialCode', param: 'SpecialCode' },
            { key: 'colorCode', param: 'ColorCode' },
            { key: 'createDate', param: 'StartDate' },
            { key: 'endDate', param: 'EndDate' },
            { key: 'userEmail', param: 'userEmail' },
            { key: 'subBrandName', param: 'SubBrandName' },
        ];

        stringFields.forEach((field) => {
            if (request[field.key as keyof ProductCommentListRequest]) {
                params += `${field.param}=${request[field.key as keyof ProductCommentListRequest]}&`;
            }
        });

        return params;
    };

    // Helper function to add complex conditional parameters
    const addComplexParams = (request: ProductCommentListRequest) => {
        let params = '';

        // Handle isAgent
        if (request.isAgent) params += `isAgent=${request.isAgent}&`;

        // Handle CustomerId
        if (request.CustomerId) params += `CustomerId=${request.CustomerId}&`;

        // Handle starScore
        if (request.starScore && request.starScore !== -1) {
            params += `Point=${request.starScore}&`;
        }

        // Handle grievanceStatus
        const grievanceId = request.grievanceStatus?.[0]?.id || 2;
        params += `GrievanceStatus=${grievanceId}&`;

        // Handle commentStatus
        if (request.commentStatus?.[0]?.id !== -1) {
            params += `ApproveStatus=${request.commentStatus?.[0]?.id}&`;
        }

        // Handle hasPhoto
        if (request.hasPhoto === 0 || request.hasPhoto === 1) {
            params += `hasPhoto=${request.hasPhoto}&`;
        }

        // Handle isShipped
        if (request.isShipped) {
            params += `isShipped=true&`;
        }

        return params;
    };

    // Helper function to add tag parameters
    const addTagParams = (request: ProductCommentListRequest) => {
        if (!request.tagName?.length) return '';

        return request.tagName.map((tag) => `ticket=${tag.id}`).join('&') + '&';
    };

    // Helper function to add operator parameter with special handling
    const addOperatorParams = (request: ProductCommentListRequest) => {
        if (!request.operator) return '';

        let params = `Operator=${request.operator}&`;
        // Remove trailing '-' if present
        if (params.endsWith('-&')) {
            params = params.slice(0, -3) + '&';
        }
        return params;
    };

    function createQueryParams(
        request: ProductCommentListRequest,
        pageValue: number,
        rowsPerPageValue: number,
        totalPageCount?: number
    ) {
        let queryParams = '';

        queryParams += addPaginationParams(pageValue, rowsPerPageValue, totalPageCount);
        queryParams += addStringParams(request);
        queryParams += addComplexParams(request);
        queryParams += addTagParams(request);
        queryParams += addOperatorParams(request);

        return queryParams;
    }

    // Helper function to transform comment item data
    const transformCommentItem = (item: any) => ({
        id: item.id || '-',
        mail: item.userEmail || '-',
        brandName: item.brandName || '-',
        special: item.specialCode || '-',
        colorCode: item.colorCode || '-',
        createDate: TimeHelper.formatDateHour(item.commentDate || '') || '-',
        status: item.status || -1,
        starScore: item.starScore || '-',
        isAgent: item.isAgent,
        threshold: item.threshold ? '%' + (item.threshold * 100).toString() : '-',
        labelProp: item.labelProp ? '%' + (item.labelProp * 100).toString() : '-',
        commentStatus: {
            text: enumCommentName[item?.status],
            className: enumCommentStatusFlowStatusName[item?.status]?.statusType,
            id: item?.status,
        },
        tag: item.tagname || '-',
        operator: item?.userOperatorName || '-',
        detay: 'Detay',
        banaAta: 'Bana Ata',
        grievanceStatus: {
            text: item?.status != 0 ? grievanceStatusLookupList[item?.grievanceStatus]?.value : '',
            className: enumCommentGrievanceStatusFlowStatusName[item?.grievanceStatus]?.statusType,
            id: item?.grievanceStatus,
        },
        images: item.images,
        IsImagesComment: item.images?.length > 0,
    });

    const getCommentList = (pageValue: number, rowsPerPageValue: number) => {
        if (params.commentStatus?.[0]?.id !== 1 && (!params.createDate || !params.endDate)) {
            Swal.fire(t('general.error') || 'Hata', t('productCommentList.pleaseEnterDateRange') || 'Lütfen tarih aralığı giriniz.', 'error');
            return;
        }

        if (loading) return;

        setLoading(true);
        const param: any = createQueryParams(params, pageValue, rowsPerPageValue);

        CommentQuery.GetCommentDataV2(param)
            .then((res: any) => {
                if (!res) return;

                const parseData = (res.data || []).map(transformCommentItem);
                setCommentList(parseData);
                setPageCount(res.count);
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const refetchGridData = (newPage?: number) => {
        if (typeof newPage === 'number' && newPage !== page) {
            setPage(newPage);
        } else {
            getCommentList(page, rowsPerPage);
        }
    };

    useEffect(() => {
        GetCommentTicketList();
    }, []);

    useEffect(() => {
        getCommentList(page, rowsPerPage);
    }, [page, rowsPerPage]);

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                title={t('productCommentList.header')}
                dontShowHome
                componentLink={'/productcomments/comment-list-v2'}
                homeIconLink={'/home'}
            />
            <Grid sx={{ marginBottom: '3rem' }}>
                <SCUILoadingLinear display={loading} />
            </Grid>
            <>
                <Grid sx={{ marginBottom: '2rem' }}>
                    <ProductCommentFilterComponentV2
                        tagNames={tagNames}
                        params={params}
                        refetchGridData={refetchGridData}
                        setParams={setParams}
                        pageCount={pageCount}
                        createQueryParams={createQueryParams}
                    />
                </Grid>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 1200 }} aria-label="product comment list table">
                        <ProductCommentListTableHeader />
                        <TableBody>
                            {(commentList || []).map((row: any) => (
                                <ProductCommentListTableBody
                                    key={row.id}
                                    setRowData={setCommentList}
                                    rowData={commentList}
                                    datas={row}
                                    refetchGridData={refetchGridData}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <SCUIPagination
                        rowsPerPageOptions={[10, 20, 50]}
                        count={pageCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onRowsPerPageChange={handleChangeRows}
                        setPage={setPage}
                    />
                </div>
            </>
        </SCUIContent>
    );
};

export default ProductCommentListV2;
